/**
 * Selo da licença no topo da Home, no lugar da barra do rodapé (que segue nas outras páginas).
 * Renovar abre o mesmo link da barra (ExpireBanner).
 */
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../hooks/useBrand';
import { useSessionStore } from '../../stores/sessionStore';
import { externalUrls } from '../../config/env';
import { genPromo } from '../../lib/promo';
import { emitActivity } from '../../lib/activity';
import { RichText } from '../../components/ui/RichText';
import type { LicenseInfo } from './homeData';
import { countText, localeOf, type Translate } from './homeText';
import { numericDateLocale } from '../../lib/format';

export function LicensePill({
  license,
  lang,
}: {
  license: LicenseInfo | null;
  lang: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const tr: Translate = (key, options) => t(key, options) as string;
  const brand = useBrand();
  const email = useSessionStore((s) => s.session?.email);
  if (!license) return null;

  const state = license.state === 'expired' ? 'off' : license.state === 'ending' ? 'warn' : 'on';
  const date = license.until
    ? license.until.toLocaleDateString(numericDateLocale(localeOf(lang)))
    : '';

  const renew = (): void => {
    emitActivity('renew.clicked', { where: 'pill' });
    // Lead-machine leva o email no link promo (desconto + atribuição), como a barra do rodapé.
    let url: string =
      brand.source === 'lead-machine' ? externalUrls.buyLeadMachine : externalUrls.buyEminer;
    if (brand.source === 'lead-machine' && email) url += '/' + genPromo({ email });
    window.open(url, '_blank');
  };

  let text: string;
  switch (license.state) {
    case 'lifetime':
      text = tr('home3b_license_lifetime', { defaultValue: 'Licença vitalícia' });
      break;
    case 'subscription':
      text = tr('home3b_license_subscription', { defaultValue: 'Assinatura ativa' });
      break;
    case 'expired':
      text = tr('home3b_license_expired', { defaultValue: 'Licença encerrada em {{date}}', date });
      break;
    default:
      text = tr('home3b_license_until', {
        defaultValue: 'Licença ativa até <b>{{date}}</b> · {{days}}',
        date,
        days: countText(tr, 'days', license.daysLeft, lang),
      });
  }

  // Movimento (23/10/2026, M3 a): a luz é um dos dois laços sem fim do produto (.ds-luz), no ritmo
  // do estado; para com "reduzir movimento". O estado continua no texto ao lado.
  const ritmo = state === 'off' ? ' ds-vencida' : state === 'warn' ? ' ds-perto' : '';

  return (
    <span className={`vx-license vx-license--${state} vx-license--dark`}>
      <span className={`vx-license__dot ds-luz${ritmo}`} aria-hidden="true" />
      <RichText as="span" html={text} />
      {license.state === 'expired' || license.state === 'ending' ? (
        <button type="button" className="vx-license__renew" onClick={renew}>
          {tr('home3b_renew', { defaultValue: 'Renovar' })}
        </button>
      ) : null}
    </span>
  );
}
